// Library Methods
import { useMemo } from "react";

// Constants
import { examCardEnums, ExamCardSectionNameType } from "src/constants/enums";

// Components
import Box from "@mui/material/Box";
import ColorVisionGraph from "./ColorVisionGraph/ColorVisionGraph";
import ContrastSensitivityGraph from "./ContrastSensitivityGraph/ContrastSensitivityGraph";
import VisualAcuityGraph from "./VisualAcuityGraph/VisualAcuityGraph";
import RAPDGraph from "./RAPDGraph/RAPDGraph";
import EstermanGraph from "./EstermanGraph/EstermanGraph";
import Full120Graph from "./Full120Graph/Full120Graph";
import Superior36Graph from "./Superior36Graph/Superior36Graph";
import VFScreeningGraph from "./VFScreeningGraph/VFScreeningGraph";
import VFThresholdingGraph from "./VFThresholdingGraph/VFThresholdingGraph";
import OcularMotilityGraph from "./OcularMotilityGraph/OcularMotilityGraph";
import IOLLensGraph from "./IOLLensGraph/IOLLensGraph";
import ProgressiveLensGraph from "./ProgressiveLensGraph/ProgressiveLensGraph";
import VideoGraph from "./VideoGraph/VideoGraph";
import SynoptophoreGraph from "./SynoptophoreGraph/SynoptophoreGraph";
import AniseikoniaGraph from "./AniseikoniaGraph/AniseikoniaGraph";

type ReportGraphType = {
  sectionName: ExamCardSectionNameType;
};

const ReportGraph = ({ sectionName }: ReportGraphType) => {
  const graph = useMemo(() => {
    switch (sectionName) {
      // videos
      case examCardEnums.VIDEO.sectionName:
        return <VideoGraph />;

      // lens simulations
      case examCardEnums.IOL_SIMULATION.sectionName:
        return <IOLLensGraph />;
      case examCardEnums.PROGRESSIVE_SIMULATION.sectionName:
        return <ProgressiveLensGraph />;

      // visual field exams
      case examCardEnums.VISUAL_FIELD_ESTERMAN.sectionName:
        return <EstermanGraph />;
      case examCardEnums.VISUAL_FIELD_FULL_120.sectionName:
        return <Full120Graph />;
      case examCardEnums.VISUAL_FIELD_SCREENING.sectionName:
        return <VFScreeningGraph />;
      case examCardEnums.VISUAL_FIELD_THRESHOLDING.sectionName:
        return <VFThresholdingGraph />;
      case examCardEnums.VISUAL_FIELD_SUPERIOR_36.sectionName:
        return <Superior36Graph />;

      // other exams
      case examCardEnums.DEVIATION_AND_CORRESPONDENCE.sectionName:
      case examCardEnums.FUSION_AND_VERGENCE.sectionName:
      case examCardEnums.STEREOPSIS.sectionName:
        return <SynoptophoreGraph />;
      case examCardEnums.COLOR_VISION.sectionName:
        return <ColorVisionGraph />;
      case examCardEnums.CONTRAST_SENSITIVITY.sectionName:
        return <ContrastSensitivityGraph />;
      case examCardEnums.VISUAL_ACUITY.sectionName:
        return <VisualAcuityGraph />;
      case examCardEnums.ANISEIKONIA.sectionName:
        return <AniseikoniaGraph />;
      case examCardEnums.RAPD.sectionName:
        return <RAPDGraph />;
      case examCardEnums.OCULAR_MOTILITY.sectionName:
        return <OcularMotilityGraph />;

      default:
        return <></>;
    }
  }, [sectionName]);

  return <Box sx={{ height: "fit-content" }}>{graph}</Box>;
};

export default ReportGraph;
